/**
 * Which preset rulesets each Standards agent type offers, under which scope, and
 * the extra context each ruleset receives when it is evaluated. Sourced from
 * `Braze::Agentic::RulesetPresets` via shared_constants.json, the same table the
 * backend reads to decide what each ruleset receives.
 * @owner @Appboy/intelligence
 */

import sharedConstants from "@lib/shared_constants";

import { isPresetRulesetType } from "./types";
import type { PresetDefinition, PresetRulesetType, QaPresetAgentType, RulesetContext, RulesetScope } from "./types";

const qaAgentSettings = sharedConstants.AI_SETTINGS.QA_AGENTS;

/** Preset type → the agent types offering it (absent when not offered) → its definition. */
const presetDefinitions: Record<
  PresetRulesetType,
  Partial<Record<QaPresetAgentType, PresetDefinition>>
> = qaAgentSettings.RULESET_PRESETS;

/**
 * Agent type → scope → context every ruleset evaluated in that scope receives,
 * preset or custom. `""` is the unscoped Campaign QA scope.
 */
const scopeContexts: Record<
  QaPresetAgentType,
  Partial<Record<RulesetScope | "", readonly RulesetContext[]>>
> = qaAgentSettings.RULESET_SCOPE_CONTEXTS;

// Every preset type, in the order the table lists them — which is the order the
// wizard's preset menus use. The table is the only place that order is decided.
const presetTypesInTableOrder = Object.keys(presetDefinitions) as PresetRulesetType[];

const toRulesetScope = (scope: RulesetScope | null): RulesetScope | undefined => scope ?? undefined;

/**
 * The preset's definition for the agent type — the scope its rulesets carry and
 * the context they receive — or undefined when that agent type does not offer
 * the preset at all. An offered Campaign QA preset has `scope: null`, so callers
 * must not read a missing scope as "not offered".
 */
export const presetDefinitionFor = (
  type: PresetRulesetType,
  agentType: QaPresetAgentType
): PresetDefinition | undefined => presetDefinitions[type][agentType];

/** The context a preset itself unlocks for the agent type (empty when it unlocks none or is not offered). */
export const presetContextsFor = (
  type: PresetRulesetType,
  agentType: QaPresetAgentType
): readonly RulesetContext[] => presetDefinitionFor(type, agentType)?.contexts ?? [];

/**
 * The preset types the agent type offers, optionally narrowed to those offered
 * under one scope, in the table's (menu) order.
 */
export const presetTypesFor = (agentType: QaPresetAgentType, scope?: RulesetScope): PresetRulesetType[] =>
  presetTypesInTableOrder.filter((type) => {
    const definition = presetDefinitionFor(type, agentType);
    return definition !== undefined && (scope === undefined || toRulesetScope(definition.scope) === scope);
  });

/**
 * The context a ruleset receives when a Standard of the agent type evaluates it:
 * its preset's, plus its scope's. Mirrors `RulesetPresets.contexts_for` for
 * persisted rulesets (the backend's implicit rulesets never reach the frontend).
 */
export const rulesetContextsFor = (
  ruleset: { type?: string; scope?: string },
  agentType: QaPresetAgentType
): RulesetContext[] => {
  const presetContexts = isPresetRulesetType(ruleset.type) ? presetContextsFor(ruleset.type, agentType) : [];
  const contextsOfScope = scopeContexts[agentType][(ruleset.scope ?? "") as RulesetScope | ""] ?? [];
  return [...new Set([...presetContexts, ...contextsOfScope])];
};

/** Whether a ruleset receives the context when a Standard of the agent type evaluates it. */
export const rulesetReceivesContext = (
  ruleset: { type?: string; scope?: string },
  agentType: QaPresetAgentType,
  context: RulesetContext
): boolean => rulesetContextsFor(ruleset, agentType).includes(context);

/**
 * The scopes in which some ruleset of the agent type receives the context
 * (undefined for the unscoped Campaign QA scope). Mirrors
 * `RulesetPresets.scopes_receiving`.
 */
export const scopesReceiving = (
  context: RulesetContext,
  agentType: QaPresetAgentType
): (RulesetScope | undefined)[] => {
  const presetScopes = presetTypesFor(agentType)
    .map((type) => presetDefinitionFor(type, agentType))
    .filter((definition): definition is PresetDefinition => !!definition?.contexts.includes(context))
    .map((definition) => toRulesetScope(definition.scope));
  const scopeScopes = (Object.entries(scopeContexts[agentType]) as [RulesetScope | "", readonly RulesetContext[]][])
    .filter(([, contexts]) => contexts.includes(context))
    .map(([scope]) => (scope === "" ? undefined : scope));
  return [...new Set([...presetScopes, ...scopeScopes])];
};

/**
 * Why a Standard of the agent type cannot save a ruleset with this preset type
 * and scope, or null when it can (including custom and unknown types). Mirrors
 * `QaAgentDefinition#validate_ruleset_presets`, worded for Operator so a tool
 * call is rejected when made rather than failing the later save.
 */
export const presetPlacementError = (
  ruleset: { name: string; type?: string | null; scope?: string },
  agentType: QaPresetAgentType
): string | null => {
  const type = ruleset.type ?? undefined;
  if (!isPresetRulesetType(type)) {
    return null;
  }
  const definition = presetDefinitionFor(type, agentType);
  if (definition === undefined) {
    return `Ruleset "${ruleset.name}" uses preset type "${type}", which this Standard does not offer. Valid types: ${presetTypesFor(agentType).join(", ")}.`;
  }
  const expectedScope = toRulesetScope(definition.scope);
  if (expectedScope !== ruleset.scope) {
    return `Ruleset "${ruleset.name}" uses preset type "${type}", which belongs on scope "${expectedScope ?? "none"}", not "${ruleset.scope ?? "none"}". Use a preset offered on that scope, or type null.`;
  }
  return null;
};
